import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core'
import { bootstrapApplication, provideProtractorTestingSupport } from '@angular/platform-browser'
import { provideRouter, Router, withHashLocation, withRouterConfig } from '@angular/router'
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http'
import { DOCUMENT } from '@angular/common'
import { browserTracingIntegration, createErrorHandler, init, TraceService } from '@sentry/angular'
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt'
import { environment } from '@view-app/environment'
import { registerElements } from '@view-app/components'
import { DynamicElementModule } from '@view-app/dynamic'
import { AppComponent } from './app/app.component'
import { appRoutes } from './app/app.routes'
import { appDynamicElements } from './app/app.dynamic-elements'
import { jwtOptionsFactory } from './app/jwt-options.factory'
import { initializeAppFactory } from './app/initialize-app.factory'
import { httpStatusInterceptor } from './app/http/interceptors/http-status/http-status-interceptor'
import { requestBodySanitizationInterceptor } from './app/http/interceptors/request-body-sanitization/request-body-sanitization-interceptor'
import { acceptLanguageHeaderInterceptor } from './app/http/interceptors/accept-language-header/accept-language-header-interceptor'
import { redirectInterceptor } from './app/http/interceptors/redirect/redirect-interceptor'
import { xHeaderInterceptor } from './app/http/interceptors/x-header/x-header-interceptor'
import { messageInterceptor } from './app/http/interceptors/message/message-interceptor'
import { RegisterCustomElementOptions } from '../../components/src/lib/custom-elements/register-custom-element-factory'

/**
 * This hack of declaring `__webpack_public_path__` lets us get around the stupid decision by
 * Angular to remove the `--deploy-url` CLI argument for `ng serve` and `ng build`.
 *
 * Without it we aren't able to dynamically load lazy chunks from another domain.
 *
 * @see https://github.com/angular/angular-cli/issues/22113#issuecomment-1004279867
 */
// eslint-disable-next-line no-var
declare let __webpack_public_path__: string
// eslint-disable-next-line @typescript-eslint/no-unused-vars
__webpack_public_path__ = environment.deployUrl

/**
 * The main function for the application.
 *
 * It configures the Sentry integration, registering the custom elements, and bootstrapping the application.
 */
async function main(): Promise<void> {
  // Enable and configure monitoring with Sentry
  if (environment?.sentry) {
    // Initialize the Sentry Angular SDK
    init({
      release: environment?.version,
      dsn: environment.sentry.dsn,
      integrations: [
        browserTracingIntegration()
      ],
      tracesSampleRate: environment.sentry.tracesSampleRate,
      tracePropagationTargets: environment.sentry.tracePropagationTargets
    })
  }

  // Enable prod mode
  if (environment.production) {
    enableProdMode()
  }

  let appRef = await bootstrapApplication(AppComponent, {
    providers: [
      // Setup Http client
      provideHttpClient(
        withInterceptors([
          httpStatusInterceptor,
          acceptLanguageHeaderInterceptor,
          messageInterceptor,
          redirectInterceptor,
          requestBodySanitizationInterceptor,
          xHeaderInterceptor
        ]),
        withInterceptorsFromDi()
      ),
      // Setup providers for the Auth0 JWT Library
      importProvidersFrom(JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory
        }
      })),
      // Setup the dynamic elements that can be rendered
      importProvidersFrom(DynamicElementModule.forRoot(appDynamicElements)),
      // Setup testing support
      provideProtractorTestingSupport(),
     // Setup router
      provideRouter(appRoutes,
        withRouterConfig({ onSameUrlNavigation: 'reload' }),
        withHashLocation()
      ),
      // Setup Sentry SDK
      { provide: ErrorHandler, useValue: createErrorHandler() },
      { provide: TraceService, deps: [ Router ] },
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => { return },
        deps: [ TraceService ],
        multi: true
      },
      // Setup the configuration of the app through attributes on the element
      {
        provide: APP_INITIALIZER,
        useFactory: initializeAppFactory,
        deps: [ DOCUMENT ],
        multi: true
      }
    ]
  })

  // Register custom elements
  let customElementOptions: RegisterCustomElementOptions = {}
  if (environment.customElementAdoptStyleSheets) {
    let document = appRef.injector.get(DOCUMENT)
    customElementOptions.styleSheets = Array.from(document.styleSheets)
  }
  registerElements(customElementOptions)
}

main().catch(e => console.error(e))
